import React, { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
    Grid,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { suscriptoorService, rentService } from '../../../services'
import CrudEmpty from '../../crud/CrudEmpty'
import HeaderSus from '../../sus/HeaderSus'
import styled from 'styled-components'
import { resolveBlock } from '../../common/blockAction'
import { BlockModal } from '../../common/BlockFeedback'

const moment = require('moment')

type NewRentParams = {
    id: string
}

export default function NewRent() {
    const { t } = useTranslation()
    const { id } = useParams<NewRentParams>()

    const [
        {
            sus,
            email,
            modality,
            start,
            end,
            block,
            seguro,
            sending,
            error,
            errorBlock,
            result,
            preview,
            previewLoading,
            cupon,
            cuponData,
            cuponMsg,
            cuponLoading,
        },
        setState,
    ] = React.useState<any>({
        sus: {},
        email: '',
        modality: 'RENT',
        start: '',
        end: '',
        block: 0,
        seguro: false,
        sending: false,
        error: '',
        // MOBD-66: bloqueo de usuario devuelto por el backend al asignar (null = sin bloqueo).
        errorBlock: null,
        result: null,
        preview: null,
        previewLoading: false,
        // Cupón validado contra POST /appsuscriptor/v2/coupon/test. El cliente
        // NO calcula el descuento — solo guarda el código y la respuesta del
        // backend para enseñar el nombre. El descuento aparece en el panel
        // preview en cuanto el siguiente disparo incluye coupon.code en el body.
        cupon: '',
        cuponData: null,
        cuponMsg: '',
        cuponLoading: false,
    })

    // Contador monotónico para invalidar respuestas tardías de testRentCoupon.
    // Cada vez que el usuario cambia algo que altera el contexto del cupón
    // (email/fechas/modalidad/código tecleado) o pide quitar el cupón,
    // incrementamos el contador. Cuando llega la respuesta del backend,
    // solo se aplica si el contador no ha cambiado desde que se disparó la
    // petición. Cierra B4: race condition por doble-click, network lag,
    // o cambio de email mid-flight que dejaba cuponData con datos del
    // contexto anterior.
    const couponReqIdRef = useRef(0)

    useEffect(() => {
        searchData()
    }, [])

    const searchData = () => {
        suscriptoorService.one(id, (data, err) => {
            if (data && data.data) {
                setState((s) => ({ ...s, ['sus']: data.data }))
            }
        })
    }

    const upd = (patch) => setState((s) => ({ ...s, ...patch }))

    // end = inicio + bloque. Solo es ayuda de fecha: el precio lo calcula el backend.
    const computeEnd = (startValue, hours) => {
        if (!startValue || !hours) return ''
        return moment(startValue).add(hours, 'hours').format('YYYY-MM-DDTHH:mm')
    }

    // datetime-local (local) -> ISO UTC para el backend.
    const toIso = (localValue) =>
        moment(localValue).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z'

    const isXarent = modality === 'XARENT'

    // Cualquier cambio que pueda invalidar el cupón validado (fechas,
    // modalidad, email, código tecleado) debe limpiar cuponData para que el
    // body de preview/create no envíe un coupon que ya no es aplicable. El
    // usuario tendrá que pulsar "Validar" de nuevo.
    //
    // B4: invalidateCoupon() es función (no objeto), porque también incrementa
    // el contador couponReqIdRef. Cada handler que invalide el cupón debe
    // bumpear el contador para descartar respuestas tardías de testRentCoupon
    // que llegaran después del cambio. Sitio de uso: `upd({ ..., ...invalidateCoupon() })`.
    const invalidateCoupon = () => {
        couponReqIdRef.current += 1
        return { cuponData: null, cuponMsg: '' }
    }

    const onChangeModality = (value) => {
        // al cambiar de modalidad se resetea el bloque y el fin calculado;
        // si pasa a XARENT (Q2) además se borra el cupón completo (input + estado).
        const goingXarent = value === 'XARENT'
        upd({
            modality: value,
            block: 0,
            end: '',
            ...(goingXarent
                ? (couponReqIdRef.current += 1, { cupon: '', cuponData: null, cuponMsg: '' })
                : invalidateCoupon()),
        })
    }

    const onChangeStart = (value) => {
        if (isXarent && block > 0) {
            upd({ start: value, end: computeEnd(value, block), ...invalidateCoupon() })
        } else {
            upd({ start: value, ...invalidateCoupon() })
        }
    }

    const onChangeBlock = (value) => {
        if (value == null) return
        upd({ block: value, end: computeEnd(start, value), ...invalidateCoupon() })
    }

    const validateCoupon = () => {
        const code = (cupon || '').trim()
        if (!code) {
            upd({ cuponMsg: t('rent.coupon.required'), cuponData: null })
            return
        }
        if (isXarent) return
        // B4: bumpear el contador ANTES de disparar la petición y capturar el
        // valor actual para compararlo cuando llegue la respuesta. Si entre
        // medias hubo otro click, otro cambio o un removeCoupon, el contador
        // habrá cambiado y descartaremos la respuesta tardía.
        couponReqIdRef.current += 1
        const reqId = couponReqIdRef.current
        upd({ cuponLoading: true, cuponMsg: '' })
        // Body alineado con preview/create + coupon.code. El backend reusa
        // CouponManagerImpl.rentTest (valida vigencia, emails, limitUses,
        // multiple_uses, fechas_rent y minDaysRent en un solo sitio).
        // modality va con el valor REAL (no hardcode 'RENT'): el endpoint
        // /coupon/test rechaza XARENT con 400 — defense-in-depth si por
        // cualquier motivo el guard isXarent de arriba se elude.
        const body = {
            user: { email: email },
            suscription: { id: id },
            modality: modality,
            start: start ? toIso(start) : null,
            end: end ? toIso(end) : null,
            coupon: { code: code },
        }
        rentService.testRentCoupon(body, (data, fail) => {
            // Descarta respuestas tardías: si el reqId capturado al disparar
            // no coincide con el actual, ignoramos por completo. Esto cierra
            // el race condition de doble-click, cambio de email mid-flight,
            // o removeCoupon mientras la petición estaba en vuelo (B4).
            if (reqId !== couponReqIdRef.current) {
                return
            }
            if (data && data.data) {
                upd({ cuponData: data.data, cuponMsg: '', cuponLoading: false })
            } else {
                upd({
                    cuponData: null,
                    cuponMsg:
                        fail && fail.mes ? fail.mes : t('rent.coupon.invalid'),
                    cuponLoading: false,
                })
            }
        })
    }

    const removeCoupon = () => {
        // B4: bump del contador para invalidar cualquier respuesta de
        // validateCoupon que pudiera estar en vuelo.
        couponReqIdRef.current += 1
        upd({ cupon: '', cuponData: null, cuponMsg: '', cuponLoading: false })
    }

    const canSave =
        email !== '' &&
        start !== '' &&
        end !== '' &&
        moment(end).isAfter(moment(start)) &&
        (!isXarent || block > 0)

    // Preview de precio con debounce. El cálculo lo sigue haciendo el backend
    // (POST /preview). Si el formulario aún no es válido, ocultamos el panel.
    // El alquiler real solo se crea al pulsar "Asignar"; este efecto NUNCA
    // persiste nada.
    useEffect(() => {
        if (!canSave) {
            if (preview !== null) upd({ preview: null })
            return
        }
        const body: any = {
            user: { email: email },
            suscription: { id: id },
            modality: modality,
            start: toIso(start),
            end: toIso(end),
            seguro: seguro,
        }
        // Cupón solo si fue validado (cuponData != null) y la modalidad sigue
        // siendo RENT. Garantiza que XARENT nunca lleva cupón en el body
        // aunque hubiera quedado uno validado de una sesión RENT previa.
        if (cuponData && !isXarent) {
            body.coupon = { code: cuponData['code'] }
        }
        upd({ previewLoading: true })
        const t = setTimeout(() => {
            rentService.preview(body, (data, fail) => {
                if (data && data.data) {
                    upd({ preview: data.data, previewLoading: false })
                } else {
                    // Si el preview falla (p.ej. usuario aún no existe en la
                    // base) no mostramos modal de error: el modal de error es
                    // exclusivo del flujo Asignar. Solo dejamos el preview en
                    // null hasta que el formulario sea válido a ojos del backend.
                    upd({ preview: null, previewLoading: false })
                }
            })
        }, 400)
        return () => clearTimeout(t)
    }, [email, modality, start, end, block, seguro, canSave, cuponData, isXarent])

    const save = () => {
        if (!canSave || sending) return
        upd({ sending: true })
        const body: any = {
            user: { email: email },
            suscription: { id: id },
            modality: modality,
            start: toIso(start),
            end: toIso(end),
            seguro: seguro,
        }
        // Body byte-idéntico al de preview: si hay cupón validado y la
        // modalidad es RENT, lo enviamos; en cualquier otro caso, el body
        // sale igual que antes del cambio (regresión protegida).
        if (cuponData && !isXarent) {
            body.coupon = { code: cuponData['code'] }
        }
        rentService.create(body, (data, fail) => {
            if (data && data.data) {
                upd({ sending: false, result: data.data })
            } else {
                // MOBD-66: si el backend adjunta un bloqueo de usuario (cliente con datos/pagos
                // pendientes), se muestra el aviso enriquecido (motivo + solución + acción) que ya
                // trae redactado, en vez del texto plano. Sin bloqueo, se mantiene el modal de antes.
                const blockInfo = resolveBlock(fail)
                if (blockInfo) {
                    upd({ sending: false, errorBlock: blockInfo })
                } else {
                    upd({
                        sending: false,
                        error:
                            fail && fail.mes
                                ? fail.mes
                                : 'No se ha podido asignar el alquiler',
                    })
                }
            }
        })
    }

    const lineAmount = (line) => {
        var v =
            line['total'] != null
                ? line['total']
                : line['price'] != null
                ? line['price']
                : 0
        return (+v).toFixed(2)
    }

    const renderForm = () => (
        <PanelForm>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        variant="standard"
                        sx={{ width: '100%' }}
                        label="Email del cliente"
                        value={email}
                        onChange={(e) =>
                            upd({
                                email: e.target.value,
                                ...invalidateCoupon(),
                            })
                        }
                        type="email"
                        InputLabelProps={{ shrink: true }}
                        autoComplete="off"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl variant="standard" sx={{ width: '100%' }}>
                        <InputLabel shrink>Modalidad</InputLabel>
                        <Select
                            value={modality}
                            onChange={(e) =>
                                onChangeModality(e.target.value)
                            }
                        >
                            <MenuItem value="RENT">RENT</MenuItem>
                            <MenuItem value="XARENT">XARENT</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        variant="standard"
                        sx={{ width: '100%' }}
                        label="Inicio"
                        value={start}
                        onChange={(e) => onChangeStart(e.target.value)}
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                {isXarent ? (
                    <Grid item xs={12} sm={6}>
                        <p style={{ margin: '0 0 6px', color: '#666' }}>
                            Bloque de uso
                        </p>
                        <ToggleButtonGroup
                            value={block}
                            exclusive
                            onChange={(e, value) => onChangeBlock(value)}
                        >
                            <ToggleButton value={4}>4 h</ToggleButton>
                            <ToggleButton value={12}>12 h</ToggleButton>
                        </ToggleButtonGroup>
                        {end !== '' && (
                            <p
                                style={{
                                    margin: '8px 0 0',
                                    color: '#666',
                                    fontSize: '0.9em',
                                }}
                            >
                                Fin:{' '}
                                {moment(end).format('DD/MM/YYYY HH:mm')}
                            </p>
                        )}
                    </Grid>
                ) : (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            sx={{ width: '100%' }}
                            label="Fin"
                            value={end}
                            onChange={(e) =>
                                upd({
                                    end: e.target.value,
                                    ...invalidateCoupon(),
                                })
                            }
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={seguro}
                                onChange={(e) =>
                                    upd({
                                        seguro: e.target.checked,
                                        ...invalidateCoupon(),
                                    })
                                }
                            />
                        }
                        label="Seguro a todo riesgo"
                    />
                </Grid>
                {/* Cupón de descuento. Q2: solo aplica a RENT; en XARENT el
                    input queda deshabilitado con tooltip y el body NUNCA lleva
                    coupon. Q6: la respuesta del test endpoint expone solo
                    {id, name, code, percentage_discount, price_discount,
                    priceDayRent} — aquí solo mostramos el name. El descuento
                    real lo añade el backend en el siguiente disparo de
                    preview como línea negativa "Descuento". */}
                <Grid item xs={12}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 10,
                        }}
                    >
                        <TextField
                            variant="standard"
                            sx={{ flex: 1 }}
                            label={t('rent.coupon.label')}
                            placeholder={t('rent.coupon.placeholder')}
                            value={cupon}
                            onChange={(e) =>
                                upd({
                                    cupon: e.target.value,
                                    ...invalidateCoupon(),
                                })
                            }
                            disabled={isXarent || cuponLoading}
                            InputLabelProps={{ shrink: true }}
                            title={
                                isXarent
                                    ? t('rent.coupon.disabledXarent')
                                    : ''
                            }
                        />
                        {cuponData ? (
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={removeCoupon}
                                disabled={cuponLoading}
                            >
                                {t('rent.coupon.remove')}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={validateCoupon}
                                disabled={
                                    isXarent ||
                                    cuponLoading ||
                                    (cupon || '').trim() === ''
                                }
                            >
                                {cuponLoading
                                    ? t('rent.coupon.validating')
                                    : t('rent.coupon.validate')}
                            </Button>
                        )}
                    </div>
                    {isXarent && (
                        <p
                            style={{
                                margin: '6px 0 0',
                                color: '#999',
                                fontSize: '0.85em',
                            }}
                        >
                            {t('rent.coupon.disabledXarent')}
                        </p>
                    )}
                    {!isXarent && cuponData && (
                        <p
                            style={{
                                margin: '6px 0 0',
                                color: '#2e7d32',
                                fontSize: '0.9em',
                            }}
                        >
                            {t('rent.coupon.valid', {
                                name:
                                    cuponData['name'] || cuponData['code'],
                            })}
                        </p>
                    )}
                    {!isXarent && !cuponData && cuponMsg !== '' && (
                        <p
                            style={{
                                margin: '6px 0 0',
                                color: '#c62828',
                                fontSize: '0.9em',
                            }}
                        >
                            {cuponMsg}
                        </p>
                    )}
                </Grid>
                {(preview || previewLoading) && (
                    <Grid item xs={12}>
                        <div
                            style={{
                                background: '#f7faf2',
                                border: '1px solid #d6e4bf',
                                borderRadius: 6,
                                padding: '12px 16px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 8,
                                }}
                            >
                                <b>Precio estimado</b>
                                <span style={{ fontSize: '0.85em', color: '#666' }}>
                                    {previewLoading
                                        ? 'Calculando…'
                                        : 'Importe final = el devuelto al asignar'}
                                </span>
                            </div>
                            {preview && preview['lines'] && preview['lines'].length > 0 && (
                                <div style={{ marginBottom: 8 }}>
                                    {preview['lines'].map((line, i) => (
                                        <Grid
                                            container
                                            key={i}
                                            style={{
                                                padding: '4px 0',
                                                fontSize: '0.95em',
                                            }}
                                        >
                                            <Grid item xs={8}>
                                                {line['concept']}
                                            </Grid>
                                            <Grid
                                                item
                                                xs={4}
                                                style={{ textAlign: 'right' }}
                                            >
                                                {lineAmount(line)} €
                                            </Grid>
                                        </Grid>
                                    ))}
                                </div>
                            )}
                            <Grid
                                container
                                style={{
                                    borderTop: '1px solid #d6e4bf',
                                    paddingTop: 6,
                                    fontWeight: 'bold',
                                    fontSize: '1.05em',
                                }}
                            >
                                <Grid item xs={8}>
                                    Total
                                </Grid>
                                <Grid item xs={4} style={{ textAlign: 'right' }}>
                                    {preview && preview['total'] != null
                                        ? (+preview['total']).toFixed(2)
                                        : '—'}{' '}
                                    €
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>
                )}
                <Grid item xs={12} style={{ textAlign: 'right' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        style={{ marginRight: '20px' }}
                        onClick={() => window.history.back()}
                    >
                        {t('general.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!canSave || sending}
                        onClick={save}
                    >
                        Asignar
                    </Button>
                </Grid>
            </Grid>
        </PanelForm>
    )

    // F1 - el precio lo calcula y devuelve el backend; aquí solo se muestra.
    const renderResult = () => (
        <PanelForm>
            <h3 style={{ marginTop: 0 }}>Alquiler asignado</h3>
            <p style={{ color: '#666' }}>
                Modalidad <b>{result['modality']}</b> ·{' '}
                {result['start']
                    ? moment(result['start']).format('DD/MM/YYYY HH:mm')
                    : ''}{' '}
                →{' '}
                {result['end']
                    ? moment(result['end']).format('DD/MM/YYYY HH:mm')
                    : ''}
            </p>
            <h4>Desglose del precio</h4>
            {result['lines'] && result['lines'].length > 0 ? (
                <div>
                    {result['lines'].map((line, i) => (
                        <Grid
                            container
                            key={i}
                            style={{
                                borderBottom: '1px solid #eee',
                                padding: '6px 0',
                            }}
                        >
                            <Grid item xs={8}>
                                {line['concept']}
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                style={{ textAlign: 'right' }}
                            >
                                {lineAmount(line)} €
                            </Grid>
                        </Grid>
                    ))}
                </div>
            ) : (
                <p style={{ color: '#666' }}>
                    Sin desglose disponible.
                </p>
            )}
            <Grid
                container
                style={{
                    padding: '10px 0',
                    fontWeight: 'bold',
                    fontSize: '1.1em',
                }}
            >
                <Grid item xs={8}>
                    Total
                </Grid>
                <Grid item xs={4} style={{ textAlign: 'right' }}>
                    {result['total'] != null
                        ? (+result['total']).toFixed(2)
                        : '0.00'}{' '}
                    €
                </Grid>
            </Grid>
            <div style={{ textAlign: 'right', marginTop: '1em' }}>
                <Button
                    variant="contained"
                    color="warning"
                    href={'/rental/edit/' + result['id']}
                >
                    Gestionar alquiler
                </Button>
            </div>
        </PanelForm>
    )

    return (
        <CrudEmpty i18n={'rent'} goback={true}>
            <Grid container>
                <Grid xs={12}>
                    <HeaderSus id={id} />
                </Grid>
                <Grid xs={12}>{result ? renderResult() : renderForm()}</Grid>
            </Grid>
            {error !== '' && (
                <ModalBack>
                    <div>
                        <h3 style={{ marginTop: 0 }}>
                            No se puede asignar el alquiler
                        </h3>
                        <p>{error}</p>
                        <Button
                            onClick={() => upd({ error: '' })}
                            variant="contained"
                            color="secondary"
                            style={{ margin: '10px 0 20px' }}
                        >
                            Entendido
                        </Button>
                    </div>
                </ModalBack>
            )}
            {/* MOBD-66: aviso de bloqueo de usuario (contrato del backend). */}
            <BlockModal
                block={errorBlock}
                onClose={() => upd({ errorBlock: null })}
            />
        </CrudEmpty>
    )
}

const ModalBack = styled.div`
    display: flex;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background: rgba(0, 0, 0, 0.4);
    z-index: 2000;
    align-items: center;
    justify-content: center;

    & div {
        background: white;
        border-radius: 6px;
        padding: 1em 2em;
        text-align: center;
        width: 600px;
        max-width: 80%;
    }
`

const PanelForm = styled.div`
    background: white;
    border-radius: 6px;
    padding: 20px;
`
